'use client';

import React from 'react';
import { Check, Send, SquarePen } from 'lucide-react';
import { Button } from '@/core/presentation/components/ui/Button';
import { Input } from '@/core/presentation/components/ui/Input';
import { OtpInput } from '@/core/presentation/components/ui/OtpInput';
import { LegalDocumentViewerModal } from '@/core/presentation/components/LegalDocumentViewerModal';
import {
  LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_PRIVACY,
  LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_TERMS,
} from '@/core/presentation/constants/legal-document-viewer-copy';
import { formatPhoneWithCountryCode } from '@/core/utils/formatPhone';
import {
  ONBOARDING_BRIDGE,
  ONBOARDING_LEGAL_PRIVACY_CHECKBOX,
  ONBOARDING_LEGAL_READ_DOCUMENT,
  ONBOARDING_LEGAL_TERMS_CHECKBOX,
  ONBOARDING_PHONE_VERIFY_BUTTON,
  ONBOARDING_PHONE_VERIFY_CHANGE_NUMBER,
  ONBOARDING_PHONE_VERIFY_CODE_SENT_TO,
  ONBOARDING_PHONE_VERIFY_OTP_LABEL,
  ONBOARDING_PHONE_VERIFY_RESEND,
  ONBOARDING_PHONE_VERIFY_RESEND_IN,
  ONBOARDING_PHONE_VERIFY_SENDING,
  ONBOARDING_PHONE_VERIFIED_STATUS,
} from '@/features/authentication/presentation/composables/constants/onboarding.constants';
import type { OnboardingFormSubmitPayload } from '@/features/authentication/data/types/onboarding-form.types';
import { useOnboardingForm } from '@/features/authentication/presentation/composables/hooks/onboardingFormHook';

const checkboxClass =
  'mt-1 h-4 w-4 shrink-0 rounded border-white/30 bg-navy-dark text-cyan focus:ring-cyan focus:ring-offset-0 focus:ring-offset-navy-dark';

export interface OnboardingFormProps {
  initialWhatsApp?: string;
  initialEmail?: string;
  initialName?: string;
  phoneVerified?: boolean;
  emailReadOnly?: boolean;
  /** Nome vindo do provedor social — somente leitura. */
  nameReadOnly?: boolean;
  /** Sincroniza rascunho de e-mail quando o pai calcula e-mail vindo do Auth (ex.: login social). */
  emailDraftHint?: string;
  authLoading: boolean;
  submitLoading: boolean;
  onWhatsAppChange: (nationalParsed: string) => void;
  onRequestSendPhoneCode: (whatsappNationalDigits: string) => Promise<boolean>;
  onResendPhoneCode: (whatsappNationalDigits: string) => Promise<boolean>;
  onConfirmLinkCode: (code: string) => Promise<boolean>;
  onChangePhoneNumberStep: () => void;
  onSubmit: (payload: OnboardingFormSubmitPayload) => void | Promise<void>;
  onValidationError: (message: string) => void;
  privacyModalBody: string;
  termsModalBody: string;
}

export const OnboardingForm: React.FC<OnboardingFormProps> = (props) => {
  const {
    copy,
    initialName,
    phoneVerified,
    emailReadOnly,
    nameReadOnly,
    authLoading,
    submitLoading,
    nameDraft,
    setNameDraft,
    companyDraft,
    setCompanyDraft,
    whatsapp,
    emailDraft,
    setEmailDraft,
    acceptedTerms,
    setAcceptedTerms,
    acceptedPrivacy,
    setAcceptedPrivacy,
    legalModal,
    setLegalModal,
    phoneFieldLocked,
    phoneVerifyStep,
    code,
    setCode,
    resendCooldownSeconds,
    canSubmitProfile,
    localPhoneVerified,
    handleSubmit,
    handleWhatsAppChange,
    handleSendPhoneVerification,
    handleResendCode,
    handleChangeNumber,
    privacyModalBody,
    termsModalBody,
    verifySendDisabled,
    phoneSmsBusy,
  } = useOnboardingForm(props);

  const recaptchaAnchor = (
    <div
      key="recaptcha-onboarding"
      id={ONBOARDING_BRIDGE.RECAPTCHA_CONTAINER_ID}
      className="sr-only"
      aria-hidden
    />
  );

  const whatsappDisplay =
    formatPhoneWithCountryCode(whatsapp) || copy.WHATSAPP_DISPLAY_FALLBACK;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="text"
          name="name"
          label={copy.FIELD_NAME_LABEL}
          placeholder={copy.FIELD_NAME_PLACEHOLDER}
          autoComplete="name"
          value={nameReadOnly ? (initialName ?? '') : nameDraft}
          onChange={
            nameReadOnly ? undefined : (ev) => setNameDraft(ev.target.value)
          }
          fullWidth
          required
          readOnly={nameReadOnly}
        />
        <Input
          type="text"
          name="company"
          label={copy.FIELD_COMPANY_LABEL}
          placeholder={copy.FIELD_COMPANY_PLACEHOLDER}
          autoComplete="organization"
          value={companyDraft}
          onChange={(ev) => setCompanyDraft(ev.target.value)}
          fullWidth
          required
        />

      <Input
        type="email"
        name="email"
        label={copy.FIELD_EMAIL_LABEL}
        placeholder={copy.FIELD_EMAIL_PLACEHOLDER}
        autoComplete="email"
        value={emailDraft}
        onChange={emailReadOnly ? undefined : (ev) => setEmailDraft(ev.target.value)}
        fullWidth
        required
        readOnly={emailReadOnly}
        />

        {phoneFieldLocked ?
          <div className="space-y-1">
            <Input
              type="tel"
              name="whatsapp"
              label={copy.FIELD_WHATSAPP_LABEL}
              placeholder={copy.FIELD_WHATSAPP_PLACEHOLDER}
              autoComplete="tel"
              inputMode="tel"
              value={formatPhoneWithCountryCode(whatsapp)}
              fullWidth
              required
              readOnly
            />
            <div className="flex justify-end" role="status" aria-live="polite">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-400">
                <Check className="h-4 w-4 shrink-0" aria-hidden />
                {ONBOARDING_PHONE_VERIFIED_STATUS}
              </span>
            </div>
          </div>
        : phoneVerifyStep === 'number' ?
          <div className="space-y-1">
            <Input
              type="tel"
              name="whatsapp"
              label={copy.FIELD_WHATSAPP_LABEL}
              placeholder={copy.FIELD_WHATSAPP_PLACEHOLDER}
              autoComplete="tel"
              inputMode="tel"
              value={formatPhoneWithCountryCode(whatsapp)}
              onChange={handleWhatsAppChange}
              fullWidth
              required
            />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={verifySendDisabled}
                loading={phoneSmsBusy || authLoading}
                loadingLabel={ONBOARDING_PHONE_VERIFY_SENDING}
                onClick={() => void handleSendPhoneVerification()}
                className="inline-flex items-center gap-2 font-medium text-cyan hover:text-cyan-light hover:underline hover:underline-offset-2 hover:bg-transparent focus-visible:underline focus-visible:underline-offset-2 focus-visible:ring-2 focus-visible:ring-cyan"
              >
                <Send className="h-4 w-4 shrink-0" aria-hidden />
                {ONBOARDING_PHONE_VERIFY_BUTTON}
              </Button>
            </div>
          </div>
        : phoneVerifyStep === 'code' ?
          <div className="space-y-4">
            <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-white/80">
              <span className="shrink-0">{ONBOARDING_PHONE_VERIFY_CODE_SENT_TO}</span>
              <span className="inline-flex min-w-0 max-w-full flex-nowrap items-center gap-2">
                <strong className="whitespace-nowrap text-white">{whatsappDisplay}</strong>
                <button
                  type="button"
                  onClick={handleChangeNumber}
                  disabled={authLoading}
                  aria-label={ONBOARDING_PHONE_VERIFY_CHANGE_NUMBER}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-cyan transition-colors hover:bg-white/10 hover:text-cyan-light focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark disabled:pointer-events-none disabled:opacity-50"
                >
                  <SquarePen className="h-4 w-4" aria-hidden />
                </button>
              </span>
            </p>
            <OtpInput
              value={code}
              onChange={setCode}
              label={ONBOARDING_PHONE_VERIFY_OTP_LABEL}
              disabled={authLoading}
            />
            <p className="text-white/60 text-sm text-right">
              <button
                type="button"
                onClick={(e) => void handleResendCode(e)}
                disabled={resendCooldownSeconds > 0 || authLoading}
                className="text-cyan font-medium hover:text-cyan-light focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-cyan"
              >
                {resendCooldownSeconds > 0
                  ? ONBOARDING_PHONE_VERIFY_RESEND_IN(resendCooldownSeconds)
                  : ONBOARDING_PHONE_VERIFY_RESEND}
              </button>
            </p>
          </div>
        : null}

        

        <fieldset className="space-y-3 rounded-lg border border-white/15 bg-white/5 px-3 py-3">
          <legend className="sr-only">{copy.LEGEND_LEGAL_ACCEPTANCE}</legend>
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-snug text-white/90">
            <input
              type="checkbox"
              name="accept_terms"
              checked={acceptedTerms}
              onChange={(ev) => setAcceptedTerms(ev.target.checked)}
              className={checkboxClass}
            />
            <span className="flex-1">
              {ONBOARDING_LEGAL_TERMS_CHECKBOX}{' '}
              <button
                type="button"
                className="font-medium text-cyan underline-offset-2 hover:underline"
                onClick={() => setLegalModal('terms')}
              >
                {ONBOARDING_LEGAL_READ_DOCUMENT}
              </button>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-snug text-white/90">
            <input
              type="checkbox"
              name="accept_privacy"
              checked={acceptedPrivacy}
              onChange={(ev) => setAcceptedPrivacy(ev.target.checked)}
              className={checkboxClass}
            />
            <span className="flex-1">
              {ONBOARDING_LEGAL_PRIVACY_CHECKBOX}{' '}
              <button
                type="button"
                className="font-medium text-cyan underline-offset-2 hover:underline"
                onClick={() => setLegalModal('privacy')}
              >
                {ONBOARDING_LEGAL_READ_DOCUMENT}
              </button>
            </span>
          </label>
        </fieldset>

        <Button
          type="submit"
          fullWidth
          size="lg"
          disabled={submitLoading || !canSubmitProfile}
          loading={submitLoading}
          loadingLabel={copy.SUBMIT_LOADING_LABEL}
        >
          {copy.SUBMIT_LABEL}
        </Button>
      </form>

      {recaptchaAnchor}

      <LegalDocumentViewerModal
        isOpen={legalModal === 'privacy'}
        onClose={() => setLegalModal(null)}
        title={LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_PRIVACY}
        body={privacyModalBody}
        contentInstanceId="privacy"
      />
      <LegalDocumentViewerModal
        isOpen={legalModal === 'terms'}
        onClose={() => setLegalModal(null)}
        title={LEGAL_DOCUMENT_VIEWER_MODAL_TITLE_TERMS}
        body={termsModalBody}
        contentInstanceId="terms"
      />
    </>
  );
};
